import { Section } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  AGRICULTURAL_PA_AVAILABLE_FUNDS,
  AGRICULTURAL_PA_PROPERTY_DETAILS,
  AGRICULTURAL_PA_PROPERTY_INFO,
  AGRICULTURAL_PA_PURCHASE_INFO,
  AGRICULTURAL_PA_EXISTING_AG_CUSTOMER,
  AGRICULTURAL_PA_BUSINESS_INFO,
  AGRICULTURAL_PA_BUSINESS_FINANCIALS,
  AGRICULTURAL_PA_BUSINESS_OWNERS,
  AGRICULTURAL_PA_LOAN_HOLDER,
  AGRICULTURAL_PA_PERSONAL_INFO,
  AGRICULTURAL_PA_BORROWER_LIVING_SITUATION,
  AGRICULTURAL_PA_BORROWER_HOME_INFO,
  AGRICULTURAL_PA_BORROWER_RENTER_INFO,
  AGRICULTURAL_PA_BORROWER_INCOME_SOURCE,
  AGRICULTURAL_PA_BORROWER_EMPLOYMENT,
  AGRICULTURAL_PA_BORROWER_RETIREMENT,
  AGRICULTURAL_PA_EXISTING_CUSTOMER_INFO,
  AGRICULTURAL_PA_BORROWER_QUESTIONS,
  AGRICULTURAL_PA_FINAL,
} from 'app/models/sections/constants';
import { ConfirmationCommercial } from '../../components/ConfirmationCommercial';
import { FormParagraphs } from '../paragraphs';
import { AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS } from '../questions/commercial-agricultural-purchase-acreage';
import {
  isEmployee,
  isEmploymentOther,
  isExistingAG,
  isHomeowner,
  isRenting,
  isRetired,
  isSelfEmployed,
} from '../fields/conditionals';
import { getCommercialBridgeLoanQuestionsByName } from '../questions/commercial-bridge-loan';
import { getCommercialDeclarationQuestions } from '../questions/declarations';
import { handleAGFormSubmit } from '../../actions/form/actions';
import { BorrowerType } from '@lenderful/domain';
import { ajaxValidateBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { Disclosure } from 'app/components/Disclaimer/Disclosure';

export const getAgriculturalPurchaseAcreageSectionById = (id: number): Section => {
  return AGRICULTURAL_PURCHASE_ACREAGE_SECTIONS.find((section) => section.id === id) as Section;
};

const AGRICULTURAL_PURCHASE_ACREAGE_SECTIONS: Section[] = [
  {
    id        : AGRICULTURAL_PA_PURCHASE_INFO,
    title     : 'Tell us about your new Ag purchase',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.homeValue,
      FieldNames.agPropertyType,
      FieldNames.expectedClosingDate,
    ]),
  },
  {
    id        : AGRICULTURAL_PA_AVAILABLE_FUNDS,
    title     : 'Available Funds',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([FieldNames.assets]),
    paragraph : FormParagraphs.commercialAvailableFunds,
  },
  {
    id        : AGRICULTURAL_PA_PROPERTY_INFO,
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.agPropertyIdentity,
      FieldNames.commercialDescription,
      FieldNames.propertyStreet,
      FieldNames.propertyStreet2,
      FieldNames.propertyCity,
      FieldNames.propertyState,
      FieldNames.propertyZip,
      FieldNames.yearTaxes,
      FieldNames.yearInsure,
    ]),
  },
  {
    id        : AGRICULTURAL_PA_PROPERTY_DETAILS,
    title     : 'Tell us more about your Ag purchase',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.landSize,
      FieldNames.numOfStructures,
      FieldNames.buildingSquareFootage,
    ]),
  },
  {
    id             : AGRICULTURAL_PA_EXISTING_AG_CUSTOMER,
    questions      : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.isAGExistingCustomer,
    ]),
  },
  {
    id        : AGRICULTURAL_PA_BUSINESS_INFO,
    title     : 'Tell us about your business',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.commercialBusinessName,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.commercialPhone,
      FieldNames.commercialEmployeeCount,
      FieldNames.commercialEntityType,
      FieldNames.commercialYearStarted,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_PA_BUSINESS_FINANCIALS,
    title     : 'Tell us about your business financials',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.commercialRevenueTrend3,
      FieldNames.commercialRevenueLastYear,
      FieldNames.commercialRevenueExplanation,
      FieldNames.commercialProfitTrend3,
      FieldNames.commercialProfitExplanation,
      FieldNames.commercialCollateralList,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_PA_BUSINESS_OWNERS,
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.commercialOwnerList,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_PA_LOAN_HOLDER,
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.commercialLoanHolder,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id       : AGRICULTURAL_PA_PERSONAL_INFO ,
    title    : 'Enter your personal information',
    questions: AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.email,
      FieldNames.phone,
      FieldNames.dateOfBirth,
    ]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_PA_BORROWER_LIVING_SITUATION,
    title     : 'What is your current housing situation?',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([FieldNames.livingRentOrOwn]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id       : AGRICULTURAL_PA_BORROWER_HOME_INFO,
    title    : 'Provide your homeowner information',
    questions: AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.livingPropertyType,
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
    ]),
    showIf: [(values) => isHomeowner(values) && !isExistingAG(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : AGRICULTURAL_PA_BORROWER_RENTER_INFO,
    title     : 'Provide your renter information',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingMonthlyRentAmount,
    ]),
    showIf : [(values) => isRenting(values) && !isExistingAG(values)],
    submitAction : (formValues) => ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : AGRICULTURAL_PA_BORROWER_INCOME_SOURCE,
    title     : 'What is your income source?',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([FieldNames.employmentType]),
    showIf: [(values) => !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_PA_BORROWER_EMPLOYMENT,
    title     : 'Tell us about your income and employment',
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.commercialEmploySameBusiness,
      FieldNames.employCompanyName,
      FieldNames.employStreet,
      FieldNames.employStreet2,
      FieldNames.employCity,
      FieldNames.employState,
      FieldNames.employZip,
      FieldNames.employTitle,
      FieldNames.employPhone,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employOvertime,
      FieldNames.employCommission,
      FieldNames.employBonus,
      FieldNames.additionalIncome,
      FieldNames.employPrevName,
      FieldNames.employPrevStreet,
      FieldNames.employPrevStreet2,
      FieldNames.employPrevCity,
      FieldNames.employPrevState,
      FieldNames.employPrevZip,
      FieldNames.employPrevTitle,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
    ]),
    showIf       : [(values) => (isEmployee(values) || isSelfEmployed(values)) && !isExistingAG(values)],
  },
  {
    id        : AGRICULTURAL_PA_BORROWER_RETIREMENT,
    questions : AGRICULTURAL_PURCHASE_ACREAGE_QUESTIONS.getByName([
      FieldNames.retirementIncome,
      FieldNames.otherIncome,
    ]),
    showIf       : [(values) => (isRetired(values) ||  isEmploymentOther(values)) && !isExistingAG(values)],
  },
  {
    id           : AGRICULTURAL_PA_EXISTING_CUSTOMER_INFO,
    title        : 'Please enter your Loan and Business Information',
    questions    : getCommercialBridgeLoanQuestionsByName([
      FieldNames.existingLoanNumber,
      FieldNames.commercialBusinessName,
      FieldNames.commercialStreet,
      FieldNames.commercialStreet2,
      FieldNames.commercialCity,
      FieldNames.commercialState,
      FieldNames.commercialZip,
      FieldNames.firstName,
      FieldNames.lastName,
      FieldNames.email,
      FieldNames.phone,
    ]),
    showIf       : [isExistingAG],
    submitAction : handleAGFormSubmit,
    submitText   : 'Submit',
  },
  {
    id           : AGRICULTURAL_PA_BORROWER_QUESTIONS,
    title        : 'Borrower Questions',
    questions    : getCommercialDeclarationQuestions(BorrowerType.PRIMARY),
    showIf: [(values) => !isExistingAG(values)],
    submitAction : handleAGFormSubmit,
    submitText   : 'Submit',
    agreement    : <Disclosure />,
  },
  {
    id              : AGRICULTURAL_PA_FINAL,
    ifNoQuestions   : ConfirmationCommercial,
    hideProgressBar : true,
  },
];
